import { useEffect, useState } from "react"
import { X } from "lucide-react"
import * as Dialog from "@radix-ui/react-dialog"
import { AuditCard } from "../benchmarkSearch/BenchmarkSearchSection"
import PageControls from "../benchmarkApi/PageControls"
import { searchLeads } from "../benchmarkApi/benchmarkClient"
import { buildLeaderboardSearchBody, mapLead } from "../benchmarkApi/reportData"
import { fieldErrorTextClass } from "../lightTheme"
import { leaderboardContent } from "./leaderboardContent"

const headingClass =
  "text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8"

const statusDotClass = {
  green: "bg-[#5DC753]",
  yellow: "bg-[#F3BF4A]",
  red: "bg-[#EE6D5E]",
}

const chipClass =
  "h-9 cursor-pointer rounded-full px-3 text-[13px] font-normal leading-5 focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#ffffff,0_0_0_4px_oklch(57.61%_0.251_258.23)]"

export default function LeaderboardSection({ countries }) {
  const [filterId, setFilterId] = useState("all")
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState(null)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState("loading")
  const [error, setError] = useState("")

  const filters = [
    { id: "all", label: "All" },
    ...countries.map((country) => ({ id: `country:${country}`, label: country })),
    ...leaderboardContent.speedFilters,
  ]

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    setStatus("loading")
    setError("")
    setRows([])
    setTotal(0)

    searchLeads(buildLeaderboardSearchBody(filterId, page), controller.signal)
      .then((payload) => {
        if (cancelled) return
        const items = Array.isArray(payload?.items) ? payload.items.map(mapLead) : []
        setRows(items)
        setTotal(typeof payload?.total === "number" ? payload.total : 0)
        setStatus("ready")
      })
      .catch((requestError) => {
        if (cancelled || requestError?.name === "AbortError") return
        setStatus("error")
        setError(requestError?.message || "The audit data could not be loaded.")
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [filterId, page])

  const selected = rows.find((row) => row.id === selectedId) ?? null

  function selectFilter(id) {
    setFilterId(id)
    setPage(1)
    setSelectedId(null)
  }

  return (
    <section className="flex w-full translate-x-[calc(50vw-50%-1.5rem)] flex-col gap-8">
      <h2 className={headingClass}>{leaderboardContent.title}</h2>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Leaderboard filters">
        {filters.map((filter) => {
          const active = filter.id === filterId
          return (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectFilter(filter.id)}
              className={`${chipClass} ${
                active
                  ? "bg-white text-[#171717] shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                  : "bg-transparent text-[#4D4D4D]"
              }`}
            >
              {filter.label}
            </button>
          )
        })}
      </div>
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left text-[14px] leading-5 text-[#4D4D4D]">
          <caption className="sr-only">{leaderboardContent.title}</caption>
          <thead>
            <tr className="border-b border-solid border-[#EBEBEB]">
              {leaderboardContent.columns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="h-9 px-2 text-left align-middle text-[14px] font-medium leading-5 text-[#4D4D4D]"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                tabIndex={0}
                aria-label={`Open audit for ${row.name}`}
                onClick={() => setSelectedId(row.id)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return
                  event.preventDefault()
                  setSelectedId(row.id)
                }}
                className="cursor-pointer transition-colors hover:bg-[#F2F2F2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[oklch(57.61%_0.251_258.23)]"
              >
                <td className="px-2 py-2.5 align-middle">
                  <span className="flex max-w-[280px] items-center gap-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-solid border-[#EBEBEB] bg-white text-[13px] font-medium leading-none text-[#171717]">
                      {row.name.slice(0, 1)}
                    </span>
                    <span className="min-w-0 whitespace-normal break-words text-[14px] font-normal leading-5 text-[#171717]">
                      {row.name}
                    </span>
                  </span>
                </td>
                <td className="px-2 py-2.5 align-middle whitespace-nowrap">{row.location}</td>
                <td className="px-2 py-2.5 align-middle">
                  <span className="inline-flex items-center gap-2 text-[14px] font-normal leading-5 text-[#171717]">
                    <span
                      className={`size-2.5 shrink-0 rounded-full ${statusDotClass[row.statusTone]}`}
                      aria-hidden="true"
                    />
                    {row.statusLabel}
                  </span>
                </td>
                <td className="px-2 py-2.5 align-middle whitespace-nowrap font-medium tabular-nums text-[#171717]">
                  {row.responseLabel}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {status === "loading" ? (
        <p className="text-[14px] font-normal leading-5 text-[#4D4D4D]">{leaderboardContent.loading}</p>
      ) : null}
      {status === "error" ? <p className={fieldErrorTextClass}>{error}</p> : null}
      {status === "ready" && rows.length === 0 ? (
        <p className="text-[14px] font-normal leading-5 text-[#4D4D4D]">{leaderboardContent.empty}</p>
      ) : null}
      <PageControls
        page={page}
        total={total}
        onPage={(nextPage) => {
          setSelectedId(null)
          setPage(nextPage)
        }}
      />
      <Dialog.Root open={selected != null} onOpenChange={(open) => { if (!open) setSelectedId(null) }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/40" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed left-1/2 top-1/2 z-[60] w-[min(720px,calc(100%-3rem))] -translate-x-1/2 -translate-y-1/2 outline-none"
          >
            <Dialog.Title className="sr-only">
              {selected ? `${selected.name} — ${selected.location}` : "Company audit"}
            </Dialog.Title>
            <div className="flex flex-col items-end gap-2">
              <Dialog.Close className="flex size-8 cursor-pointer items-center justify-center rounded-full text-white hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
                <X className="size-4" aria-hidden="true" />
                <span className="sr-only">Close</span>
              </Dialog.Close>
              {selected ? <AuditCard company={selected} /> : null}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  )
}
